/**
 * @file InvoiceProposalDialog.js
 * @version 1.2.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-08
 * @modified 2026-10-30
 *
 * "INV-xxxx was raised on the old quantity" (Cam, 8 Oct 2026). A supplied
 * quantity changed after the order was invoiced, before the invoice went out
 * to the customer: the system works out what the invoice should become —
 * each line's quantity old → new, its total and VAT at its own price and
 * rate, the invoice's totals old → new — and shows it here, so nobody types
 * the correction (services/invoiceSupplyService).
 *
 * Two buttons: Apply (the caller saves with `invoice_changes` and the invoice
 * is edited after the save — "Apply these changes and save") and Continue
 * (nothing saved; back to the form as it was — "Continue editing the bill",
 * "Continue editing"). A line that could not be matched to the order, or an
 * invoice whose amounts include VAT, is listed to check by hand.
 *
 * 1.1.0 (2026-10-08, review): the figures are SALE prices, sent under names
 * the purchase-cost redactor leaves alone (sale_unit_price; totals net / vat
 * / gross), so a user without costs.view sees them too; an invoice that
 * cannot be edited here is shown as a notice ("can't be changed here").
 *
 * 1.2.0 (2026-10-30, Cam — SO-D10442): a UNITS correction's offer (`kind:
 * 'units'`, services/invoiceSupplyService.decideUnits) — each line's units
 * old → new and its unit price old → new (by the line's answer to "what was
 * the price per?"); the quantity stays. Titled "raised on the old units".
 */

import React from 'react';
import Modal from './Modal';
import { useSlideOutButton } from './SlideOutButton';
import { formatQuantity } from '../utils/numberFormatters';
import { money } from '../utils/invoiceProposals';
import './BillPoCostDialog.css';

function InvoiceProposalBody({ proposals, notices, stale, busy, error, onOpenInvoice }) {
  useSlideOutButton([busy, proposals.length]);
  const numbers = proposals.map(p => p.invoice_no).filter(Boolean);
  const units = proposals.some(p => p.kind === 'units');
  return (
    <div className="invoice-proposal-body">
      <p className="invoice-proposal-intro">
        <strong>{numbers.join(', ') || 'The invoice'}</strong> {numbers.length > 1 ? 'were' : 'was'} raised on the old {units ? 'units' : 'quantity'}.
        {units
          ? ` These are the changes ${numbers.length > 1 ? 'they need' : 'it needs'} to match the corrected units — the quantity stays, the price follows your answer on each line, and the line total and VAT are worked at the invoice's own VAT rate.`
          : ` These are the changes ${numbers.length > 1 ? 'they need' : 'it needs'} to match the new supplied quantity — at the invoice's own prices and VAT rates.`}
      </p>
      {stale && (
        <p className="invoice-proposal-hint">
          <span className="material-symbols-rounded">sync_problem</span>
          The invoice or its order changed since these were worked out. They have been worked out again — check them.
        </p>
      )}
      {proposals.map(p => (
        <div key={p.invoice_id} className="invoice-proposal-invoice">
          <h4>
            {p.invoice_no}{p.order_number ? ` · ${p.order_number}` : ''}{p.in_xero ? ' · in Xero' : ''}
            {onOpenInvoice && (
              <button type="button" className="btn-icon" title={`Open ${p.invoice_no} in the invoice editor`} onClick={() => onOpenInvoice(p)} disabled={busy}>
                <span className="material-symbols-rounded">open_in_new</span>
              </button>
            )}
          </h4>
          <div className="table-container">
            <table className="data-table invoice-proposal-table">
              <thead>
                <tr>
                  <th>Line</th>
                  <th className="num">Unit price</th>
                  <th className="num">Quantity</th>
                  <th className="num">Line total</th>
                  <th className="num">VAT</th>
                </tr>
              </thead>
              <tbody>
                {p.lines.map(l => (
                  <tr key={l.invoice_line_id}>
                    <td>
                      {l.description || l.item_code || '—'}
                      {l.units && (
                        <div className="invoice-proposal-units">{l.units.old} → <strong>{l.units.new}</strong></div>
                      )}
                    </td>
                    <td className="num">
                      {l.old_sale_unit_price != null && Number(l.old_sale_unit_price) !== Number(l.sale_unit_price)
                        ? <>{money(l.old_sale_unit_price)} → <strong>{money(l.sale_unit_price)}</strong></>
                        : money(l.sale_unit_price)}
                    </td>
                    <td className="num">
                      {Number(l.old_quantity) === Number(l.new_quantity)
                        ? formatQuantity(l.new_quantity)
                        : <>{formatQuantity(l.old_quantity)} → <strong>{formatQuantity(l.new_quantity)}</strong></>}
                    </td>
                    <td className="num">{money(l.old_line_total)} → <strong>{money(l.new_line_total)}</strong></td>
                    <td className="num">{money(l.old_tax)} → <strong>{money(l.new_tax)}</strong></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="invoice-proposal-totals">
            Invoice total {money(p.old_totals?.gross)} → <strong>{money(p.new_totals?.gross)}</strong>
            {' '}(before VAT {money(p.old_totals?.net)} → {money(p.new_totals?.net)}, VAT {money(p.old_totals?.vat)} → {money(p.new_totals?.vat)})
          </p>
          {p.unmatched?.length > 0 && (
            <p className="invoice-proposal-hint">
              <span className="material-symbols-rounded">help</span>
              {p.unmatched.map(u => u.product || 'A line').join(', ')} could not be matched to a line of {p.invoice_no} — check it by hand.
            </p>
          )}
        </div>
      ))}
      {(notices || []).filter(n => n.blocked).map(n => (
        <p key={`n-${n.invoice_id}`} className="invoice-proposal-hint">
          <span className="material-symbols-rounded">info</span>{n.blocked}.
        </p>
      ))}
      <p className="invoice-proposal-note">
        Nothing else on the invoice changes. {proposals.some(p => p.in_xero) ? 'An invoice in Xero is changed there too, with this reason on its history.' : ''}
      </p>
      {error && (
        <div className="bill-po-cost-error" role="alert">
          <span className="material-symbols-rounded">error</span>
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}

/**
 * @param {object} props
 * @param {Array<object>} props.proposals — the server's actionable proposals
 * @param {Array<object>} [props.notices]
 * @param {boolean} [props.stale]
 * @param {boolean} [props.busy]
 * @param {string|null} [props.error]
 * @param {string} [props.applyLabel] — default "Apply these changes and save"
 * @param {string} [props.continueLabel] — default "Continue editing"
 * @param {() => void} props.onApply
 * @param {() => void} props.onContinue — nothing saved
 * @param {(proposal: object) => void} [props.onOpenInvoice]
 */
function InvoiceProposalDialog({
  proposals, notices = [], stale = false, busy = false, error = null,
  applyLabel = 'Apply these changes and save', continueLabel = 'Continue editing',
  onApply, onContinue, onOpenInvoice = null
}) {
  return (
    <Modal
      isOpen
      onClose={busy ? () => {} : onContinue}
      title={proposals.some(p => p.kind === 'units') ? 'Invoice raised on the old units' : 'Invoice raised on the old quantity'}
      size="lg"
      className="invoice-proposal"
      footer={
        <div className="buttons slideout">
          <div>
            <button className="btn-slideout" onClick={onContinue} disabled={busy} title="Nothing is saved — back to where you were">
              <div className="icon-wrapper"><span className="material-symbols-rounded">edit_note</span></div>
              <span className="btn-label">{continueLabel}</span>
            </button>
          </div>
          <div>
            <button className="btn-slideout btn-slideout-primary" onClick={onApply} disabled={busy || !proposals.length}>
              <div className="icon-wrapper">
                <span className={`material-symbols-rounded${busy ? ' spinning' : ''}`}>{busy ? 'progress_activity' : 'task_alt'}</span>
              </div>
              <span className="btn-label">{busy ? 'Saving…' : applyLabel}</span>
            </button>
          </div>
        </div>
      }
    >
      <InvoiceProposalBody proposals={proposals} notices={notices} stale={stale} busy={busy} error={error} onOpenInvoice={onOpenInvoice} />
    </Modal>
  );
}

export default InvoiceProposalDialog;
